// Separate fetch path prevents axios/reporting failures from reporting themselves.
const recent = new Map();
const seen = new WeakSet();

const CHUNK_LOAD_ERROR = /Loading (CSS )?chunk .+ failed|Failed to fetch dynamically imported module|Importing a module script failed/;
const CHUNK_RELOAD_KEY = 'vdb_chunk_reload';

// After a deploy, open tabs request chunk hashes that no longer exist. Reload
// to pick up the new build instead of reporting an error, at most once per
// path per minute so a chunk that is genuinely broken can't loop.
export function recoverFromChunkError(error, path = window.location.pathname + window.location.search) {
  if (!CHUNK_LOAD_ERROR.test(String(error?.message || error || ''))) return false;
  try {
    const last = JSON.parse(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 'null');
    if (last?.path === path && Date.now() - last.at < 60000) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    return false; // Without storage we can't guard against a reload loop.
  }
  window.location.assign(path);
  return true;
}

let activeBetaSurface = null;

/** Set by BetaBanner while a beta tool is on screen so its errors are tagged in the inbox. */
export function setBetaSurface(surface) {
  activeBetaSurface = surface || null;
}

export function captureError(error, context = {}) {
  if (typeof window === 'undefined') return;
  if (activeBetaSurface && !context.component) {
    context = { ...context, component: activeBetaSurface, code: context.code || 'beta' };
  }
  // Crawler renders can't fetch /api/* (robots.txt) and report phantom 500s.
  if (typeof document !== 'undefined' && document.documentElement?.dataset?.crawler === '1') return;
  if (error && typeof error === 'object') {
    if (seen.has(error)) return;
    seen.add(error);
  }
  const message = String(error?.message || error || 'Unknown application error').slice(0, 2000);
  const endpoint = String(context.endpoint || window.location.pathname).split(/[?#]/)[0];
  if (endpoint.includes('/issues/')) return;
  const key = `${context.source || 'browser'}:${endpoint}:${message}`;
  const now = Date.now();
  if (now - (recent.get(key) || 0) < 60000) return;
  if (recent.size > 100) recent.clear();
  recent.set(key, now);
  try {
    const token = localStorage.getItem('access_token');
    const body = JSON.stringify({ ...context, source: context.source || 'browser', message,
      endpoint, release: process.env.VUE_APP_RELEASE || null,
      stack: typeof error?.stack === 'string' ? error.stack.slice(0, 6000) : undefined });
    void fetch('/api/issues/capture', {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body, keepalive: true,
    }).then(response => {
      // A stale app session must not hide the browser failure. Retry once anonymously.
      if (response.status === 401 && token) return fetch('/api/issues/capture', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true,
      });
    }).catch(() => {});
  } catch { /* Storage/browser may be unavailable; never break the application. */ }
}

export function installBrowserErrorReporting() {
  window.addEventListener('error', event => {
    const error = event.error || event.message;
    if (error && !recoverFromChunkError(error)) captureError(error, { source: 'browser' });
  });
  window.addEventListener('unhandledrejection', event => {
    if (!recoverFromChunkError(event.reason)) captureError(event.reason, { source: 'browser' });
  });
}
